import { useEffect } from "react";
import { runOnboardingAgent } from "./onboardingAgent";
import { env } from "./env";

/* useFoyrBundleAutoIngest — mounted once at App root.
 *
 * Listens for `foyrmind:foyrBundleArrived` (fired by bridge.js when
 * the Chrome extension's content script forwards a Foyr Neo bundle
 * into our /api/foyr-bundle/{id} endpoint and the bridge polls it
 * down into localStorage[foyrmind.foyrBundles.v1]).
 *
 * On arrival: run the Onboarding Agent on the raw bundle, persist
 * the typed graph keyed by the linked-project id, and fire
 * `foyrmind:projectChanged` so every cockpit surface re-renders.
 *
 * Zero human action. The extension does the capture, the bridge
 * does the polling, this hook does the typed-ontology mapping. */

interface BundleRecord {
  bundle: string;
  received_at: string;
  size: number;
  ingested_at?: string;
}

export function useFoyrBundleAutoIngest(): void {
  useEffect(() => {
    function ingestAvailable(): void {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem("foyrmind.foyrBundles.v1");
        if (!raw) return;
        const bundlesMap = JSON.parse(raw) as Record<string, BundleRecord>;

        const userRaw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
        let userProjects = userRaw ? (JSON.parse(userRaw) as Array<{ id: string; foyr_project_id?: string; name: string }>) : [];

        const graphsRaw = window.localStorage.getItem(env.VITE_LS_PROJECT_GRAPHS);
        const graphs = graphsRaw ? (JSON.parse(graphsRaw) as Record<string, { ingested_at?: string }>) : {};

        // Auto-link projects we've never seen — the extension's
        // first forward of a previously-unknown Foyr project id
        // should land in the studio switcher without the user
        // needing to visit /admin/onboard.
        let userProjectsDirty = false;
        for (const foyrId of Object.keys(bundlesMap)) {
          if (userProjects.some((p) => p.foyr_project_id === foyrId)) continue;
          const rec = bundlesMap[foyrId];
          if (!rec) continue;
          let displayName = `Foyr · ${foyrId.slice(-6)}`;
          try {
            const obj = JSON.parse(rec.bundle) as Record<string, unknown>;
            const sd = obj["sceneData"] as Record<string, unknown> | undefined;
            const nm = sd && typeof sd["projectName"] === "string" ? (sd["projectName"] as string) : null;
            if (nm) displayName = nm;
          } catch { /* ignore parse failure — keep fallback name */ }
          const id = `foyr-${foyrId}`;
          userProjects.push({
            id, foyr_project_id: foyrId, name: displayName,
            // Surface as a ProjectSummary the switcher can render.
            ...({
              city: "—", client: "Linked from Foyr Neo",
              client_email: "—", bhk: "—", budgetINR: 0,
              deadline: "", alerts: 0, status: "draft",
              thumbnailKey: "livingSofa",
              source: `https://neo.foyr.com/toolv2/?project=${foyrId}`,
              created_at: new Date().toISOString(),
            } as Record<string, unknown>),
          });
          userProjectsDirty = true;
        }
        if (userProjectsDirty) {
          window.localStorage.setItem(env.VITE_LS_USER_PROJECTS, JSON.stringify(userProjects));
        }

        let mutated = false;
        for (const up of userProjects) {
          const foyrId = up.foyr_project_id;
          if (!foyrId) continue;
          const rec = bundlesMap[foyrId];
          if (!rec || !rec.bundle) continue;
          // Idempotent: skip if we've already ingested this exact
          // received_at for this project.
          const prev = graphs[up.id];
          if (prev && (prev as { ingested_received_at?: string }).ingested_received_at === rec.received_at) continue;
          try {
            const parsed = JSON.parse(rec.bundle) as Record<string, unknown>;
            const result = runOnboardingAgent(parsed, { id: up.id, name: up.name });
            (graphs as Record<string, unknown>)[up.id] = {
              graph: result.graph,
              schema: result.schema,
              trace: result.trace,
              stats: result.stats,
              ingested_received_at: rec.received_at,
              ingested_at: new Date().toISOString(),
            };
            mutated = true;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[FoyrMind] auto-ingest parse failed for", foyrId, err);
          }
        }
        if (mutated) {
          window.localStorage.setItem(env.VITE_LS_PROJECT_GRAPHS, JSON.stringify(graphs));
          window.dispatchEvent(new Event("foyrmind:projectChanged"));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[FoyrMind] auto-ingest sweep failed", err);
      }
    }
    // Run once at mount + every time bridge.js fires the event.
    ingestAvailable();
    window.addEventListener("foyrmind:foyrBundleArrived", ingestAvailable);
    // Belt-and-braces poll every 20s so the cockpit catches bundles
    // even if the event was missed (page just loaded etc.).
    const t = window.setInterval(ingestAvailable, 20_000);
    return () => {
      window.removeEventListener("foyrmind:foyrBundleArrived", ingestAvailable);
      window.clearInterval(t);
    };
  }, []);
}
